import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { withTranslation } from 'react-i18next';

import { switchContext, refreshActiveContext } from '../../store/activeContext/actions';
import SwitchContextConfirmModal from '../../components/Common/SwitchContextConfirmModal';

/**
 * Sidebar org switcher — the primary affordance for changing the active
 * organization. Replaces the old "PAGES" header at the top of the sidebar
 * with a card showing the active org name + a chevron-down. Clicking opens
 * a dropdown with every org the user is a member of (and "Global View" when
 * permitted); selecting one triggers the same confirmation modal +
 * hard-reload flow as the topbar profile menu.
 */
const SidebarOrgSwitcher = ({ t }) => {
  const [open, setOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState(null);
  const dispatch = useDispatch();

  const ctx = useSelector((s) => s.ActiveContext) || {};
  const {
    activeOrganizationId,
    activeOrganizationName,
    isGlobalView,
    memberships = [],
    canEnterGlobalView = false,
    switching,
  } = ctx;

  const aggregateOrgCount = memberships.filter((m) => m && m.hasAggregateView).length;
  const canSwitch = memberships.length > 1 || canEnterGlobalView;

  const orgLabel = isGlobalView
    ? t('shared.global_view.label', 'Global View')
    : activeOrganizationName || t('shared.organization', 'Organization');
  const sectionLabel = t('shared.organization', 'Organization').toUpperCase();

  const requestSwitch = (organizationId, name) => {
    if (switching) return;
    setOpen(false);
    setPendingSwitch({ organizationId, name });
  };

  const cancelSwitch = () => {
    if (switching) return;
    setPendingSwitch(null);
  };

  const confirmSwitch = () => {
    if (!pendingSwitch) return;
    dispatch(
      switchContext(pendingSwitch.organizationId, (targetPath) => {
        setPendingSwitch(null);
        // Match the topbar switcher: hard reload to drop every page's
        // in-memory state. SPA navigation here was leaving stale data
        // visible from the previous org.
        window.location.href = targetPath;
      })
    );
  };

  return (
    <>
      <div className="px-2">
        <Dropdown
          isOpen={open && canSwitch}
          toggle={() => canSwitch && setOpen((o) => !o)}
          className="w-100"
        >
          <DropdownToggle
            tag="button"
            type="button"
            disabled={!canSwitch}
            onClick={() => {
              // Pull a fresh envelope the moment the user looks for orgs —
              // catches newly granted memberships and revoked permissions
              // without waiting for a focus event.
              if (!open) dispatch(refreshActiveContext());
            }}
            className="btn w-100 text-start d-flex align-items-center p-2"
            style={{
              border: '1px solid rgba(57,128,192,0.20)',
              backgroundColor: 'rgba(57,128,192,0.04)',
              borderRadius: 8,
              cursor: canSwitch ? 'pointer' : 'default',
            }}
            title={
              canSwitch
                ? t('shared.org_switcher.tooltip', 'Switch organization')
                : undefined
            }
          >
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0 me-2"
              style={{
                width: 36,
                height: 36,
                backgroundColor: 'rgba(57,128,192,0.12)',
                borderRadius: 8,
              }}
            >
              <i
                className={`bx ${isGlobalView ? 'bx-globe' : 'bx-buildings'} font-size-18`}
                style={{ color: '#3980c0' }}
              />
            </div>
            <div className="flex-grow-1 lh-sm overflow-hidden">
              <div
                className="text-uppercase text-muted"
                style={{ fontSize: 10, letterSpacing: 0.6 }}
              >
                {sectionLabel}
              </div>
              <div
                className="fw-semibold text-truncate"
                style={{ color: '#1f2937', fontSize: 14 }}
                title={orgLabel}
              >
                {orgLabel}
              </div>
            </div>
            {canSwitch && (
              <i
                className="bx bx-chevron-down ms-1 text-muted font-size-18 flex-shrink-0"
                style={{
                  transform: open ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.15s ease',
                }}
              />
            )}
          </DropdownToggle>
          <DropdownMenu className="mt-1" style={{ minWidth: 260 }}>
            {canEnterGlobalView && (
              <>
                <DropdownItem
                  onClick={() =>
                    requestSwitch(null, t('shared.global_view.label', 'Global View'))
                  }
                  disabled={switching || isGlobalView}
                  active={isGlobalView}
                >
                  <i className="bx bx-globe text-muted font-size-18 align-middle me-2" />
                  <span className="align-middle">
                    {t('shared.global_view.label', 'Global View')}
                  </span>
                  <span className="text-muted ms-2 font-size-12">
                    ({aggregateOrgCount}{' '}
                    {t('shared.global_view.orgs_short', 'orgs')})
                  </span>
                  {switching && isGlobalView && (
                    <Spinner size="sm" className="ms-2" />
                  )}
                </DropdownItem>
                <div className="dropdown-divider"></div>
              </>
            )}
            {memberships.length > 0 && (
              <>
                <DropdownItem
                  header
                  className="text-uppercase text-muted font-size-11"
                >
                  {t('shared.org_switcher.section_header', 'Organizations')}
                </DropdownItem>
                {memberships.map((m) => {
                  const isActive =
                    !isGlobalView && activeOrganizationId === m.organizationId;
                  return (
                    <DropdownItem
                      key={`sb-org-${m.organizationId}`}
                      onClick={() =>
                        requestSwitch(m.organizationId, m.organizationName)
                      }
                      disabled={switching || isActive}
                      active={isActive}
                    >
                      <i className="bx bx-buildings text-muted font-size-18 align-middle me-2" />
                      <span className="align-middle">{m.organizationName}</span>
                      {switching && isActive && (
                        <Spinner size="sm" className="ms-2" />
                      )}
                    </DropdownItem>
                  );
                })}
              </>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>
      <SwitchContextConfirmModal
        isOpen={!!pendingSwitch}
        targetName={pendingSwitch?.name}
        switching={switching}
        onConfirm={confirmSwitch}
        onCancel={cancelSwitch}
      />
    </>
  );
};

SidebarOrgSwitcher.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(SidebarOrgSwitcher);
